import { type RefObject, useEffect, useRef } from "react";

/**
 * Today as a detent on the tank schedule's sideways scroll (Tyler, 2026-10-07: "if I scroll out to
 * January 2027 and then flick it back towards today, instead of blowing past today and going to the
 * past it sticks on today and requires another flick/scroll to keep going").
 *
 * A scroll that would carry the chart across today's resting place, the place the Today button puts
 * it, stops there:
 *
 *   - a flick's glide, a wheel or a touchpad swipe, and the Back and Forward buttons stop at today for
 *     the rest of that gesture, and the next one carries on past it;
 *   - a finger or the scrollbar's thumb still held down sticks at today until it has gone a thumb's
 *     width further, then follows again, so a slow drag is never simply refused.
 *
 * It works from where the scroll lands, so every way of scrolling is caught the same way. A scroll back
 * the way it came is never held, nor the browser bringing a bar the keyboard reached into view.
 */

/** Which side of today's resting place a scroll position is on, or 0 within a pixel of it. */
export type Side = -1 | 0 | 1;

export function sideOf(position: number, rest: number): Side {
  const off = position - rest;
  return Math.abs(off) < 1 ? 0 : off < 0 ? -1 : 1;
}

/** A scroll from one side of today that lands on it or beyond it. */
export const crosses = (from: Side, to: Side) => from !== 0 && to !== from;

/** How far, in screen pixels, a held finger or thumb goes on past today before the chart follows. */
export const RELEASE_PX = 48;

/** A wheel or touchpad gesture ends once its events stop for this long; its glide is part of it. */
export const QUIET_MS = 200;

/**
 * Ends a flick's glide where it is. A browser carries on gliding after the page sets the scroll, so the
 * chart would be pulled back to today frame after frame; a scroller that stops scrolling for a moment
 * forgets its glide. Its scrollbar's room is kept meanwhile, so nothing under it moves. Returns what
 * turns it back on at once, for a new touch or wheel in that moment.
 */
const GLIDE_STOP_MS = 150;

function stopGlide(el: HTMLElement, at: number): () => void {
  const bar = el.offsetHeight - el.clientHeight - el.clientTop * 2;
  el.style.overflowX = "hidden";
  if (bar > 0) el.style.paddingBottom = `${bar}px`;
  el.scrollLeft = at;
  let done = false;
  const restore = () => {
    if (done) return;
    done = true;
    clearTimeout(timer);
    el.style.overflowX = "";
    el.style.paddingBottom = "";
    el.scrollLeft = at;
  };
  const timer = setTimeout(restore, GLIDE_STOP_MS);
  return restore;
}

interface Held {
  /** The gesture held, and the side it came from: going back that way lets go at once. */
  gesture: number;
  from: Side;
  /** Where a held finger was when the chart stopped at today. */
  fingerAt: number | null;
}

/**
 * Hooks the detent to a scroller. `rest` says where today rests, in the scroller's own pixels. Call
 * the returned `check` first in the scroller's scroll handler, `gesture` before a scroll the page
 * makes on a press, such as paging with the Back and Forward buttons, and `sync` after the page sets
 * the scroll itself.
 */
export function useTodayDetent(scroller: RefObject<HTMLElement | null>, rest: () => number) {
  const restAt = useRef(rest);
  restAt.current = rest;
  const state = useRef({
    gesture: 0,
    side: 0 as Side,
    held: null as Held | null,
    /** A finger on the screen, or the mouse holding the scrollbar's thumb. */
    down: null as null | { kind: "touch"; x: number } | { kind: "thumb" },
    wheelAt: Number.NEGATIVE_INFINITY,
    /** Turns the scroller back on early, while a glide is being stopped. */
    restore: null as null | (() => void),
    /** A gesture never held: the browser bringing a bar the keyboard reached into view. */
    free: -1,
  });

  const gesture = () => {
    const s = state.current;
    s.gesture += 1;
    s.held = null;
    s.restore?.();
    s.restore = null;
  };

  /**
   * After the page itself puts the chart somewhere (opening at today, the Today button, a zoom, more
   * days loaded at the left), so that move is not taken for a scroll across today.
   */
  const sync = () => {
    const el = scroller.current;
    if (el) state.current.side = sideOf(el.scrollLeft, restAt.current());
  };

  const check = () => {
    const el = scroller.current;
    if (!el) return;
    const s = state.current;
    const at = restAt.current();
    const side = sideOf(el.scrollLeft, at);
    if (s.free === s.gesture) {
      s.side = side;
      return;
    }
    const held = s.held?.gesture === s.gesture ? s.held : null;
    if (held) {
      // Going back the way it came is never held; a finger or thumb held down lets go a thumb's
      // width past today.
      const past =
        s.down?.kind === "touch" && held.fingerAt !== null
          ? Math.abs(s.down.x - held.fingerAt)
          : s.down?.kind === "thumb"
            ? (Math.abs(el.scrollLeft - at) * el.clientWidth) / Math.max(1, el.scrollWidth)
            : 0;
      if (side === held.from || past >= RELEASE_PX) {
        s.held = null;
        s.side = side;
        return;
      }
      if (side !== 0) el.scrollLeft = at;
      s.side = 0;
      return;
    }
    if (crosses(s.side, side)) {
      s.held = {
        gesture: s.gesture,
        from: s.side,
        fingerAt: s.down?.kind === "touch" ? s.down.x : null,
      };
      // A glide is stopped; a finger or thumb still down is only held, as it may go on past.
      if (s.down) {
        if (side !== 0) el.scrollLeft = at;
      } else s.restore = stopGlide(el, at);
      s.side = 0;
      return;
    }
    s.side = side;
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: set up once; everything it reads is a ref
  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const s = state.current;
    state.current.side = sideOf(el.scrollLeft, restAt.current());

    const onTouchStart = (e: TouchEvent) => {
      gesture();
      s.down = { kind: "touch", x: e.touches[0]?.clientX ?? 0 };
    };
    const onTouchMove = (e: TouchEvent) => {
      if (s.down?.kind === "touch") s.down.x = e.touches[0]?.clientX ?? s.down.x;
    };
    // The glide after a flick stays part of the gesture the finger began.
    const onTouchEnd = (e: TouchEvent) => {
      if (e.touches.length === 0) s.down = null;
    };
    // A press on the scrollbar, below the chart's own area, begins a gesture of the thumb.
    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType === "touch") return;
      gesture();
      const box = el.getBoundingClientRect();
      if (e.clientY - box.top > el.clientTop + el.clientHeight) s.down = { kind: "thumb" };
    };
    // The keyboard moving to a bar out of view brings it into view, wherever that is.
    const onFocusIn = (e: FocusEvent) => {
      if (e.target instanceof HTMLElement && e.target.matches(":focus-visible")) {
        gesture();
        s.free = s.gesture;
      }
    };
    const onPointerUp = () => {
      if (s.down?.kind === "thumb") s.down = null;
    };
    // A wheel or touchpad gesture that would cross today stops there, before it is drawn past it, and
    // the rest of it, its glide included, is spent there.
    const onWheel = (e: WheelEvent) => {
      const dx = e.deltaX || (e.shiftKey ? e.deltaY : 0);
      if (!dx) return;
      const now = performance.now();
      if (now - s.wheelAt > QUIET_MS) gesture();
      s.wheelAt = now;
      if (s.held?.gesture === s.gesture) {
        const at = restAt.current();
        const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? el.clientWidth : 1;
        // Back the way it came goes; on past today is spent.
        if (sideOf(el.scrollLeft + dx * unit, at) !== s.held.from) e.preventDefault();
        return;
      }
      const at = restAt.current();
      const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? el.clientWidth : 1;
      const from = sideOf(el.scrollLeft, at);
      if (crosses(from, sideOf(el.scrollLeft + dx * unit, at))) {
        e.preventDefault();
        s.held = { gesture: s.gesture, from, fingerAt: null };
        s.side = 0;
        el.scrollLeft = at;
      }
    };

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: true });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    el.addEventListener("touchcancel", onTouchEnd, { passive: true });
    el.addEventListener("pointerdown", onPointerDown);
    el.addEventListener("focusin", onFocusIn);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("mouseup", onPointerUp);
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("focusin", onFocusIn);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("mouseup", onPointerUp);
      el.removeEventListener("wheel", onWheel);
    };
  }, []);

  return { check, gesture, sync };
}
